<template>
  <teleport to="body">
    <div class="toast-portal" aria-live="polite" aria-atomic="false">
      <transition-group name="toast-anim" tag="div" class="toast-stack">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast-card"
          :class="`toast-${toast.type}`"
          role="alert"
          @click="remove(toast.id)"
        >
          <!-- Icon -->
          <div class="toast-icon-wrap">
            <div class="toast-icon-circle">
              <CheckCircle v-if="toast.type === 'success'" :size="22" />
              <WifiOff     v-else-if="toast.type === 'network'" :size="22" />
              <ShieldAlert v-else-if="toast.type === 'error'" :size="22" />
              <Info        v-else-if="toast.type === 'info'" :size="22" />
              <AlertTriangle v-else :size="22" />
            </div>
          </div>

          <!-- Content -->
          <div class="toast-content">
            <p class="toast-type-label">{{ typeLabel(toast.type) }}</p>
            <p class="toast-message">{{ toast.message }}</p>
          </div>

          <!-- Close -->
          <button class="toast-close" @click.stop="remove(toast.id)" :aria-label="'إغلاق'">
            <X :size="14" />
          </button>

          <!-- Progress Bar -->
          <div class="toast-progress-bar">
            <div
              class="toast-progress-fill"
              :style="{ width: toast.progress + '%' }"
            ></div>
          </div>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script setup>
import { useToast } from '@/composables/useToast';
import { CheckCircle, ShieldAlert, Info, AlertTriangle, X, WifiOff } from 'lucide-vue-next';

const { toasts, remove } = useToast();

const typeLabel = (type) => {
  const labels = {
    success: 'تمت العملية',
    error: 'تنبيه',
    warning: 'تحذير',
    info: 'معلومة',
    network: 'خطأ في الاتصال',
  };
  return labels[type] || 'إشعار';
};
</script>

<style scoped>
/* =====================================================
   TOAST PORTAL — Premium RTL Toast Notification System
   ===================================================== */

.toast-portal {
  position: fixed;
  top: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  pointer-events: none;
  width: min(480px, calc(100vw - 2rem));
}

.toast-stack {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

/* ---- Card ---- */
.toast-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.1rem 1.4rem;
  border-radius: 16px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  overflow: hidden;
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  direction: rtl;
  text-align: right;
}

.toast-card:hover {
  transform: scale(1.01);
}

/* ---- Type Colours ---- */
.toast-success {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.92) 0%, rgba(5, 150, 105, 0.92) 100%);
  box-shadow: 0 12px 40px rgba(16, 185, 129, 0.3), 0 2px 8px rgba(0,0,0,0.2);
}
.toast-error {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.93) 0%, rgba(220, 38, 38, 0.93) 100%);
  box-shadow: 0 12px 40px rgba(239, 68, 68, 0.3), 0 2px 8px rgba(0,0,0,0.2);
}
.toast-warning {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(217, 119, 6, 0.95) 100%);
  box-shadow: 0 12px 40px rgba(245, 158, 11, 0.3), 0 2px 8px rgba(0,0,0,0.2);
}
.toast-info {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.93) 0%, rgba(37, 99, 235, 0.93) 100%);
  box-shadow: 0 12px 40px rgba(59, 130, 246, 0.3), 0 2px 8px rgba(0,0,0,0.2);
}
.toast-network {
  background: linear-gradient(135deg, rgba(107, 114, 128, 0.93) 0%, rgba(75, 85, 99, 0.93) 100%);
  box-shadow: 0 12px 40px rgba(107, 114, 128, 0.25), 0 2px 8px rgba(0,0,0,0.2);
}

/* ---- Icon ---- */
.toast-icon-wrap {
  flex-shrink: 0;
}

.toast-icon-circle {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* ---- Content ---- */
.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-type-label {
  margin: 0 0 2px;
  font-size: 0.7rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.75);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.toast-message {
  margin: 0;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.5;
  word-break: break-word;
}

/* ---- Close ---- */
.toast-close {
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #fff;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  padding: 0;
}

.toast-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* ---- Progress ---- */
.toast-progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 0 0 16px 16px;
}

.toast-progress-fill {
  height: 100%;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 0 0 16px 16px;
  transition: width 0.1s linear;
}

/* ---- Animations ---- */
.toast-anim-enter-active {
  transition: all 0.45s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.toast-anim-leave-active {
  transition: all 0.3s cubic-bezier(0.55, 0, 1, 0.45);
}
.toast-anim-enter-from {
  opacity: 0;
  transform: translateY(-24px) scale(0.92);
}
.toast-anim-leave-to {
  opacity: 0;
  transform: translateY(-12px) scale(0.95);
}
.toast-anim-move {
  transition: transform 0.35s ease;
}
</style>
